import { useState } from 'react'
import { ChevronDown, Download, FileJson, FileSpreadsheet, Loader2, Upload } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { downloadFormTemplate } from '@/lib/formTemplateFile'

interface ImportFormMenuProps {
  /** Open the JSON import dialog. Runs on this page — it needs no builder. */
  onImportJson: () => void
  /**
   * Go to the Excel on-ramp. Not run here on purpose: the spreadsheet import
   * hydrates the BUILDER with an unsaved draft for review rather than saving a
   * form, so it only means anything on the editor screen.
   */
  onImportExcel: () => void
}

/**
 * The Import control beside New Form on Form Definitions — the two ways to
 * create a form from a file, behind one button.
 *
 * A Popover rather than a dropdown menu because this repo has no
 * `@radix-ui/react-dropdown-menu`, and the popover-as-menu pattern is already
 * how `ViewModeSwitcher` and `AccountMenu` do it. Adding a dependency for two
 * items would not earn its weight.
 */
export function ImportFormMenu({ onImportJson, onImportExcel }: ImportFormMenuProps) {
  const [open, setOpen] = useState(false)
  const [templateBusy, setTemplateBusy] = useState(false)
  const [templateError, setTemplateError] = useState<string | null>(null)

  async function handleDownloadTemplate() {
    setTemplateBusy(true)
    setTemplateError(null)
    try {
      await downloadFormTemplate()
    } catch (e) {
      setTemplateError(e instanceof Error ? e.message : 'Could not build the template.')
    } finally {
      setTemplateBusy(false)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant='outline'>
          <Upload className='mr-2 h-4 w-4' />
          Import
          <ChevronDown className='ml-2 h-4 w-4 text-muted-foreground' />
        </Button>
      </PopoverTrigger>

      <PopoverContent align='end' className='w-80 p-1'>
        <button
          type='button'
          className='flex w-full gap-3 rounded-md p-2.5 text-left transition-colors hover:bg-muted'
          onClick={() => {
            setOpen(false)
            onImportJson()
          }}
        >
          <FileJson className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
          <span className='min-w-0'>
            <span className='block text-sm font-medium'>Form file (.json)</span>
            <span className='block text-xs text-muted-foreground'>
              A form exported from the builder. Creates it straight away.
            </span>
          </span>
        </button>

        <div className='my-1 border-t' />

        <button
          type='button'
          className='flex w-full gap-3 rounded-md p-2.5 text-left transition-colors hover:bg-muted'
          onClick={() => {
            setOpen(false)
            onImportExcel()
          }}
        >
          <FileSpreadsheet className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
          <span className='min-w-0'>
            <span className='block text-sm font-medium'>Excel spreadsheet</span>
            <span className='block text-xs text-muted-foreground'>
              Fill in the template, then upload it. The form opens in the builder for review
              before you save.
            </span>
          </span>
        </button>

        {/* Sits under the Excel item, aligned with its text, and deliberately
            does NOT close the popover: downloading the template is a step
            BEFORE importing, so the menu stays open to be used next. */}
        <div className='px-2.5 pb-2 pl-[2.3rem]'>
          <button
            type='button'
            onClick={handleDownloadTemplate}
            disabled={templateBusy}
            className='inline-flex items-center gap-1.5 text-xs font-medium text-primary underline underline-offset-2 disabled:opacity-60'
          >
            {templateBusy ? (
              <Loader2 className='h-3 w-3 animate-spin' />
            ) : (
              <Download className='h-3 w-3' />
            )}
            {templateBusy ? 'Building template…' : 'Download the template'}
          </button>
          {templateError && <p className='mt-1 text-xs text-destructive'>{templateError}</p>}
        </div>
      </PopoverContent>
    </Popover>
  )
}
